// Tenant-wide task smart-view counts (GET /api/tasks/counts) shared by the Tasks page
// sidebar and the app-sidebar nav badge (my overdue count). Refreshes on login, on task WS
// events and on a slow interval — counts are advisory, the Tasks page refetches its own list.

import { fetchTaskCounts } from '@/lib/api/tasks';
import { getAuthToken, getAuthUser, subscribeAuthStateChange } from '@/lib/api/token-store';
import { subscribeUserThreadEvents } from '@/lib/realtime/chat-realtime-service';

const REFRESH_INTERVAL_MS = 60000;
const WS_DEBOUNCE_MS = 800;

let counts = null;
let started = false;
let refreshTimer = null;
let wsDebounceTimer = null;
const listeners = new Set();

function emit() {
  for (const listener of listeners) {
    try {
      listener(counts);
    } catch {
      /* ignore listener failures */
    }
  }
}

// Identity the cached counts belong to. Counts are "assigned to me" numbers, so a profile
// switch must drop them immediately — not merely replace them when the new user's first
// fetch succeeds (a failed first fetch would leave A's badge on B's session).
let countsOwnerUserId = '';

function dropCountsIfOwnerChanged(currentUserId) {
  if (counts === null || countsOwnerUserId === currentUserId) return;
  counts = null;
  countsOwnerUserId = '';
  emit();
}

export async function refreshTaskCounts() {
  if (!getAuthToken()) {
    if (counts !== null) {
      counts = null;
      countsOwnerUserId = '';
      emit();
    }
    return null;
  }
  // Identity guard: drop responses that started under a different user — a profile switch
  // mid-flight must not land user A's counts in user B's badge.
  const requestUserId = String(getAuthUser()?.id || '').trim();
  dropCountsIfOwnerChanged(requestUserId);
  try {
    const next = await fetchTaskCounts();
    if (String(getAuthUser()?.id || '').trim() !== requestUserId) {
      return counts;
    }
    counts = next;
    countsOwnerUserId = requestUserId;
    emit();
  } catch {
    /* keep the previous (same-user) counts; the next tick retries */
  }
  return counts;
}

function scheduleWsRefresh() {
  if (wsDebounceTimer) return;
  wsDebounceTimer = setTimeout(() => {
    wsDebounceTimer = null;
    void refreshTaskCounts();
  }, WS_DEBOUNCE_MS);
}

function ensureStarted() {
  if (started || typeof window === 'undefined') return;
  started = true;
  void refreshTaskCounts();
  refreshTimer = setInterval(() => void refreshTaskCounts(), REFRESH_INTERVAL_MS);
  // Never cleared — the store lives for the app session, like the other module stores.
  void refreshTimer;
  subscribeAuthStateChange(() => void refreshTaskCounts());
  subscribeUserThreadEvents((detail) => {
    if (String(detail?.action || '').startsWith('task')) scheduleWsRefresh();
  });
}

/** Last known counts (null until the first successful fetch). */
export function getTaskCounts() {
  ensureStarted();
  dropCountsIfOwnerChanged(String(getAuthUser()?.id || '').trim());
  return counts;
}

export function subscribeTaskCounts(listener) {
  if (typeof listener !== 'function') return () => {};
  ensureStarted();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
